<template>
  <div class="algoritma-form">
    <!-- Header -->
    <div class="page-header">
      <h1>{{ isEdit ? 'Edit Alur Algoritma Klinis' : 'Tambah Alur Algoritma Klinis' }}</h1>
    </div>

    <!-- Main Content -->
    <div class="form-content">
      <!-- Nama Algoritma -->
      <div class="form-section">
        <label class="form-label">
          Nama Alur Algoritma Klinis <span class="required">*</span>
        </label>
        <a-input
          v-model:value="form.nama"
          placeholder="Nama alur algoritma klinis"
          size="large"
          class="nama-input"
        />
      </div>

      <!-- Upload Diagram -->
      <div class="form-section">
        <label class="form-label">Gambar Diagram</label>
        <div class="upload-section">
          <div v-if="form.currentImageUrl && !form.previewUrl" class="current-image">
            <img :src="getFileUrl(form.currentImageUrl)" alt="diagram" />
            <div class="image-actions">
              <a-button type="link" @click="showDiagramPreview">
                <eye-outlined /> Preview
              </a-button>
              <a-button type="link" danger @click="removeCurrentImage">
                <delete-outlined /> Hapus
              </a-button>
            </div>
          </div>
          <div v-else-if="form.previewUrl" class="preview-image">
            <img :src="form.previewUrl" alt="preview" />
            <div class="image-actions">
              <a-button type="link" @click="showDiagramPreview">
                <eye-outlined /> Preview
              </a-button>
              <a-button type="link" danger @click="removeImage">
                <delete-outlined /> Hapus
              </a-button>
            </div>
          </div>
          <a-upload
            v-else
            :before-upload="onBeforeUpload"
            :show-upload-list="false"
            accept="image/*"
          >
            <button class="upload-btn"><upload-outlined /> UNGGAH</button>
          </a-upload>
        </div>
      </div>

      <!-- Struktur Klasifikasi -->
      <div class="form-section">
        <label class="form-label">Struktur Klasifikasi</label>
        <div class="tree-container">
          <!-- Empty State -->
          <div v-if="localNodes.length === 0" class="empty-tree">
            <table class="tree-table">
              <thead>
                <tr>
                  <th class="col-tingkat">Tingkat</th>
                  <th class="col-klasifikasi">Klasifikasi</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="td-tingkat">Tingkat 1</td>
                  <td class="td-klasifikasi">
                    <button class="add-btn" @click="showCreateNodeModal(null)">
                      <plus-outlined /> TAMBAH
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Tree Structure -->
          <table v-else class="tree-table">
            <thead>
              <tr>
                <th class="col-tingkat">Tingkat</th>
                <th class="col-klasifikasi">Klasifikasi</th>
              </tr>
            </thead>
            <tbody>
              <!-- Level 1 -->
              <tr>
                <td class="td-tingkat">Tingkat 1</td>
                <td class="td-klasifikasi">
                  <div class="klasifikasi-items">
                    <div
                      v-for="node in rootNodes"
                      :key="node.tempId || node.id"
                      class="klasifikasi-item"
                    >
                      <NodeCard
                        :node="node"
                        :algoritma-list="algoritmaList"
                        :kalkulator-list="kalkulatorList"
                        @edit="showEditNodeModal"
                        @delete="handleDeleteNode"
                      />
                    </div>
                  </div>
                </td>
              </tr>

              <!-- Subsequent Levels -->
              <template v-for="(lvl, idx) in levels" :key="idx">
                <tr>
                  <td class="td-tingkat">Tingkat {{ lvl.level }}</td>
                  <td class="td-klasifikasi">
                    <div class="klasifikasi-items">
                      <div
                        v-for="(branch, bidx) in lvl.branches"
                        :key="bidx"
                        class="klasifikasi-item"
                      >
                        <div class="path-badges">
                          <template
                            v-for="(segment, sidx) in splitPath(branch.pathLabel)"
                            :key="sidx"
                          >
                            <span class="path-badge">{{ segment }}</span>
                            <right-outlined
                              v-if="sidx < splitPath(branch.pathLabel).length - 1"
                              class="path-arrow"
                            />
                          </template>
                        </div>
                        <div class="branch-content">
                          <NodeCard
                            v-if="branch.node"
                            :node="branch.node"
                            :algoritma-list="algoritmaList"
                            :kalkulator-list="kalkulatorList"
                            @edit="showEditNodeModal"
                            @delete="handleDeleteNode"
                          />
                          <button
                            v-else
                            class="add-btn"
                            @click="showCreateNodeModal(branch.parentNode, branch.parentOpsi)"
                          >
                            <plus-outlined /> TAMBAH
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          type="primary"
          danger
          size="large"
          class="save-btn"
          :loading="submitting"
          @click="handleSubmit"
        >
          SIMPAN
        </button>
        <button size="large" class="preview-btn" @click="showPreview">
          <eye-outlined /> LIHAT PREVIEW ALUR
        </button>
      </div>
    </div>

    <!-- Create/Edit Node Modal -->
    <NodeModal
      :visible="nodeModalVisible"
      :editing-node="editingNode"
      :parent-node="parentNodeForModal"
      :parent-opsi="parentOpsiForModal"
      :algoritma-list="algoritmaList"
      :kalkulator-list="kalkulatorList"
      :current-algoritma-id="algoritmaId"
      :nodes="localNodes"
      @update:visible="nodeModalVisible = $event"
      @submit="handleNodeModalSubmit"
    />

    <!-- Diagram Preview Modal -->
    <a-modal
      title="Preview Diagram"
      :visible="diagramPreviewVisible"
      :footer="null"
      @cancel="diagramPreviewVisible = false"
      width="80%"
      centered
    >
      <div class="diagram-preview-container">
        <img
          :src="form.previewUrl || getFileUrl(form.currentImageUrl)"
          alt="diagram preview"
          class="diagram-preview-image"
        />
      </div>
    </a-modal>

    <a-modal
      title="Preview Alur Algoritma"
      :visible="previewVisible"
      :footer="null"
      @cancel="previewVisible = false"
      width="520px"
      centered
    >
      <div class="mobile-preview-container">
        <div v-if="localNodes.length === 0" class="mobile-preview-empty">
          Belum ada klasifikasi untuk ditampilkan.
        </div>
        <div v-else class="mobile-preview-flow">
          <div class="mobile-preview-toolbar">
            <button class="mobile-preview-ulang-btn" @click="previewResetFlow">
              <reload-outlined /> Ulang
            </button>
          </div>
          <template
            v-for="(node, cardIndex) in previewActiveCards"
            :key="node.id || node.tempId || cardIndex"
          >
            <!-- End marker: solid blue -->
            <div v-if="node.ditandaiTerakhir" class="mobile-preview-card mobile-preview-card--end">
              <p class="mobile-preview-card-title">{{ node.namaKlasifikasi }}</p>
              <div
                v-if="node.opsiKlasifikasi && node.opsiKlasifikasi.length > 0"
                class="mobile-preview-end-badges"
              >
                <span
                  v-for="opsi in node.opsiKlasifikasi"
                  :key="opsi"
                  class="mobile-preview-end-badge"
                  >{{ opsi }}</span
                >
              </div>
            </div>
            <!-- Has options: white + blue border -->
            <div
              v-else-if="node.opsiKlasifikasi && node.opsiKlasifikasi.length > 0"
              class="mobile-preview-card mobile-preview-card--options"
            >
              <p class="mobile-preview-card-title">{{ node.namaKlasifikasi }}</p>
              <div class="mobile-preview-options-row">
                <button
                  v-for="(opsi, opsiIdx) in node.opsiKlasifikasi"
                  :key="opsiIdx"
                  class="mobile-preview-option-btn"
                  :class="{
                    'mobile-preview-option-btn--selected':
                      previewSelectedOptions[cardIndex] === opsiIdx,
                  }"
                  @click="previewSelectOption(cardIndex, opsiIdx)"
                >
                  {{ opsi }}
                </button>
              </div>
            </div>
            <!-- No options: white + pink border -->
            <div v-else class="mobile-preview-card mobile-preview-card--no-options">
              <p class="mobile-preview-card-title">{{ node.namaKlasifikasi }}</p>
            </div>
            <!-- Arrow between cards -->
            <div
              v-if="cardIndex < previewActiveCards.length - 1"
              class="mobile-preview-arrow"
              :class="{
                'mobile-preview-arrow--blue':
                  node.ditandaiTerakhir ||
                  (node.opsiKlasifikasi && node.opsiKlasifikasi.length > 0),
                'mobile-preview-arrow--pink':
                  !node.ditandaiTerakhir &&
                  (!node.opsiKlasifikasi || node.opsiKlasifikasi.length === 0),
              }"
            >
              <svg width="40" height="30" viewBox="0 0 40 30" xmlns="http://www.w3.org/2000/svg">
                <line
                  x1="20"
                  y1="0"
                  x2="20"
                  y2="20"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                />
                <polyline
                  points="12,14 20,26 28,14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </template>
          <div ref="previewScrollAnchor" />
        </div>
      </div>
    </a-modal>

    <!-- Delete Confirmation Modal -->
    <a-modal
      title="Hapus Klasifikasi?"
      :visible="deleteModalVisible"
      :confirm-loading="deleteModalLoading"
      @ok="confirmDeleteNode"
      @cancel="cancelDelete"
      ok-text="Ya, Hapus"
      cancel-text="Batal"
      ok-type="danger"
      centered
    >
      <p>Klasifikasi ini dan semua anaknya akan dihapus. Lanjutkan?</p>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import {
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
  EyeOutlined,
  RightOutlined,
  ReloadOutlined,
} from '@ant-design/icons-vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import { mobileCmsAlgoritmaAPI, mobileCmsGuidelineAPI } from '@/services/axios/perki/mobileCms'
import NodeCard from './components/NodeCard.vue'
import NodeModal from './components/NodeModal.vue'

const FILES_BASE_URL = process.env.VUE_APP_PERKI_FILES_URL || 'http://localhost:3000/files'

export default {
  name: 'MobileCmsAlgoritmaKlinisForm',
  components: {
    UploadOutlined,
    DeleteOutlined,
    PlusOutlined,
    // MinusOutlined,
    EyeOutlined,
    RightOutlined,
    ReloadOutlined,
    NodeCard,
    NodeModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const algoritmaId = computed(() => route.params.id || null)
    const isEdit = computed(() => !!route.params.id)

    const initialLoading = ref(false)
    const submitting = ref(false)
    const formRef = ref(null)
    const previewVisible = ref(false)
    const diagramPreviewVisible = ref(false)

    const form = reactive({
      nama: '',
      deskripsi: '',
      imageFile: null,
      previewUrl: '',
      currentImageUrl: '',
    })

    const initialFormSnapshot = ref('')

    // Nodes
    const localNodes = ref([])
    const algoritmaList = ref([])
    const kalkulatorList = ref([])
    let tempIdCounter = 0

    const serializeNodesForSnapshot = nodes => {
      return [...nodes]
        .map(n => ({
          id: n.id || null,
          tempId: n.tempId || null,
          parentId: n.parentId || null,
          parentTempId: n.parentTempId || null,
          parentOpsi: n.parentOpsi || '',
          namaKlasifikasi: n.namaKlasifikasi || '',
          opsiKlasifikasi: [...(n.opsiKlasifikasi || [])],
          ditandaiTerakhir: !!n.ditandaiTerakhir,
          algoritmaTerkaitId: n.algoritmaTerkaitId || '',
          kalkulatorTerkaitId: n.kalkulatorTerkaitId || '',
        }))
        .sort((a, b) => {
          const keyA = `${a.id || ''}-${a.tempId || ''}-${a.namaKlasifikasi}`
          const keyB = `${b.id || ''}-${b.tempId || ''}-${b.namaKlasifikasi}`
          return keyA.localeCompare(keyB)
        })
    }

    const buildCurrentSnapshot = () =>
      JSON.stringify({
        nama: form.nama || '',
        deskripsi: form.deskripsi || '',
        currentImageUrl: form.currentImageUrl || '',
        hasNewImageFile: !!form.imageFile,
        hasPreviewImage: !!form.previewUrl,
        nodes: serializeNodesForSnapshot(localNodes.value),
      })

    const captureInitialSnapshot = () => {
      initialFormSnapshot.value = buildCurrentSnapshot()
    }

    const hasFormChanges = computed(() => {
      if (!initialFormSnapshot.value) return false
      return buildCurrentSnapshot() !== initialFormSnapshot.value
    })

    const unsavedChangesMessage =
      'Anda memiliki perubahan pada formulir algoritma yang belum disimpan. Yakin ingin meninggalkan halaman ini?'

    const confirmLeaveIfNeeded = () => {
      if (!hasFormChanges.value) return true
      return window.confirm(unsavedChangesMessage)
    }

    const handleBeforeUnload = e => {
      if (!hasFormChanges.value) return
      e.preventDefault()
      e.returnValue = ''
    }

    onBeforeRouteLeave((to, from, next) => {
      if (confirmLeaveIfNeeded()) {
        next()
      } else {
        next(false)
      }
    })

    // Node Modal
    const nodeModalVisible = ref(false)
    const editingNode = ref(null)
    const parentNodeForModal = ref(null)
    const parentOpsiForModal = ref('')

    // Delete Modal
    const deleteModalVisible = ref(false)
    const deleteModalLoading = ref(false)
    const nodeToDelete = ref(null)

    const getFileUrl = path => {
      if (!path) return ''
      if (path.startsWith('http')) return path
      return `${FILES_BASE_URL}/${path}`
    }

    const removeImage = () => {
      form.imageFile = null
      form.previewUrl = ''
    }

    const removeCurrentImage = () => {
      form.currentImageUrl = ''
    }

    const onBeforeUpload = file => {
      form.imageFile = file
      form.previewUrl = URL.createObjectURL(file)
      return false
    }

    const rootNodes = computed(() => {
      return localNodes.value.filter(n => !n.parentId && !n.parentTempId)
    })

    // Build levels for flat rendering
    const buildLevels = () => {
      const result = []
      const getNodeIdentifier = node => node?.id || node?.tempId
      const visitedNodes = new Set()
      const MAX_DEPTH = 20

      // Level 1: Root nodes
      const level1Nodes = localNodes.value.filter(n => !n.parentId && !n.parentTempId)
      if (level1Nodes.length === 0) return result

      // Build subsequent levels
      const buildNextLevel = (currentLevelBranches, currentLevel) => {
        if (currentLevelBranches.length === 0) return
        if (currentLevel > MAX_DEPTH) return // Safety check for max depth

        const nextLevelBranches = []

        currentLevelBranches.forEach(({ node, parentPath }) => {
          if (!node || node.ditandaiTerakhir) return

          const myId = getNodeIdentifier(node)

          // Check for circular reference
          if (visitedNodes.has(myId)) return
          visitedNodes.add(myId)

          if (!node.opsiKlasifikasi || node.opsiKlasifikasi.length === 0) {
            // No options - still create branch for adding children
            const children = localNodes.value.filter(n => {
              const parentId = n.parentId || n.parentTempId
              return (
                ((parentId && parentId === myId) ||
                  (n.parentId && node.id && n.parentId === node.id) ||
                  (n.parentTempId && node.tempId && n.parentTempId === node.tempId)) &&
                !n.parentOpsi
              )
            })

            // Path label shows parent path (e.g., "Tes" or "Tes -> Child")
            const branchPath = parentPath || node.namaKlasifikasi

            if (children.length > 0) {
              // Has children - add them
              children.forEach(child => {
                const childId = getNodeIdentifier(child)
                // Skip if child would create circular reference
                if (visitedNodes.has(childId)) return

                const childPath = `${branchPath} -> ${child.namaKlasifikasi}`
                nextLevelBranches.push({
                  node: child,
                  pathLabel: branchPath,
                  parentPath: childPath,
                })
              })
            } else {
              // No children yet - add tombol tambah branch
              nextLevelBranches.push({
                node: null,
                pathLabel: branchPath,
                parentPath: branchPath,
                parentNode: node,
                parentOpsi: null,
              })
            }
          } else {
            // Has options, create branches for each option
            node.opsiKlasifikasi.forEach(opsi => {
              const children = localNodes.value.filter(n => {
                const parentId = n.parentId || n.parentTempId
                return (
                  ((parentId && parentId === myId) ||
                    (n.parentId && node.id && n.parentId === node.id) ||
                    (n.parentTempId && node.tempId && n.parentTempId === node.tempId)) &&
                  n.parentOpsi === opsi
                )
              })
              const branchPath = parentPath
                ? `${parentPath} : ${opsi}`
                : `${node.namaKlasifikasi} : ${opsi}`
              if (children.length > 0) {
                children.forEach(child => {
                  const childId = getNodeIdentifier(child)
                  // Skip if child would create circular reference
                  if (visitedNodes.has(childId)) return

                  const childPath = parentPath
                    ? `${parentPath} : ${opsi} -> ${child.namaKlasifikasi}`
                    : `${node.namaKlasifikasi} : ${opsi} -> ${child.namaKlasifikasi}`
                  nextLevelBranches.push({
                    node: child,
                    pathLabel: branchPath,
                    parentPath: childPath,
                  })
                })
              } else {
                nextLevelBranches.push({
                  node: null,
                  pathLabel: branchPath,
                  parentPath: branchPath,
                  parentNode: node,
                  parentOpsi: opsi,
                })
              }
            })
          }
        })

        if (nextLevelBranches.length > 0) {
          result.push({
            level: currentLevel + 1,
            branches: nextLevelBranches,
          })
          buildNextLevel(nextLevelBranches, currentLevel + 1)
        }
      }

      // Start with level 1 branches
      const level1Branches = level1Nodes.map(node => ({
        node,
        pathLabel: '',
        parentPath: node.namaKlasifikasi,
      }))

      buildNextLevel(level1Branches, 1)
      return result
    }

    const levels = computed(() => buildLevels())

    const getAlgoritmaNamePreview = id => {
      const alg = algoritmaList.value.find(a => a.id === id)
      return alg?.nama || 'Unknown'
    }

    const filteredAlgoritmaList = computed(() => {
      return algoritmaList.value.filter(a => a.id !== algoritmaId.value)
    })

    const loadAlgoritma = async () => {
      if (!algoritmaId.value) return

      initialLoading.value = true
      try {
        const response = await mobileCmsAlgoritmaAPI.getById(algoritmaId.value)
        console.log('API Response:', response.data)
        const data = response.data.data
        console.log('Data:', data)
        console.log('Nodes:', data.nodes)
        form.nama = data.algoritma?.nama || ''
        form.deskripsi = data.algoritma?.deskripsi || ''
        form.currentImageUrl = data.algoritma?.imageDiagram || ''
        localNodes.value = data.nodes || []
        captureInitialSnapshot()
      } catch (error) {
        message.error('Gagal memuat data algoritma')
        console.error(error)
        router.push('/mobile-cms/algoritma-klinis')
      } finally {
        initialLoading.value = false
      }
    }

    const loadAlgoritmaList = async () => {
      try {
        const response = await mobileCmsAlgoritmaAPI.getAll()
        algoritmaList.value = response.data.data || []
      } catch (error) {
        console.error('Gagal memuat list algoritma', error)
      }
    }

    const loadKalkulatorList = async () => {
      try {
        const response = await mobileCmsGuidelineAPI.getCalculatorOptions()
        kalkulatorList.value = response.data.data || []
      } catch (error) {
        console.error('Gagal memuat list kalkulator', error)
      }
    }

    const generateTempId = () => {
      tempIdCounter++
      return `temp_${Date.now()}_${tempIdCounter}`
    }

    const showCreateNodeModal = (parentNode, parentOpsi = '') => {
      editingNode.value = null
      parentNodeForModal.value = parentNode
      parentOpsiForModal.value = parentOpsi
      nodeModalVisible.value = true
    }

    const showEditNodeModal = node => {
      editingNode.value = node
      parentNodeForModal.value = null
      parentOpsiForModal.value = ''
      nodeModalVisible.value = true
    }

    const handleNodeModalSubmit = async ({ nodeData, editingNode: editing, done, error }) => {
      try {
        if (editing) {
          if (editing.id && algoritmaId.value) {
            try {
              // For update endpoint, empty string explicitly clears related algoritma.
              const updatePayload = {
                ...nodeData,
                algoritmaTerkaitId: nodeData.algoritmaTerkaitId ?? '',
                kalkulatorTerkaitId: nodeData.kalkulatorTerkaitId ?? '',
              }

              console.log('Updating node on server:', editing.id, updatePayload)
              const updateResponse = await mobileCmsAlgoritmaAPI.updateNode(
                editing.id,
                updatePayload,
              )
              console.log('Update response:', updateResponse.data)

              // Re-sync from backend so reopened form always reflects persisted state.
              await loadAlgoritma()
            } catch (err) {
              console.error('Failed to update node on server', err)
              error()
              return
            }
          } else {
            // Temporary/local node: update in-memory only.
            const nodesCopy = [...localNodes.value]
            const index = nodesCopy.findIndex(
              n => (editing.id && n.id === editing.id) || n.tempId === editing.tempId,
            )
            if (index !== -1) {
              const oldNode = nodesCopy[index]
              const oldOpsi = oldNode.opsiKlasifikasi || []
              const newOpsi = nodeData.opsiKlasifikasi || []
              const removedOpsi = oldOpsi.filter(o => !newOpsi.includes(o))

              localNodes.value[index] = {
                ...nodesCopy[index],
                ...nodeData,
              }

              // Clean up children whose parentOpsi was removed
              if (removedOpsi.length > 0) {
                const editingId = editing.id || editing.tempId
                const nodesToRemove = new Set()

                const markDescendants = (parentId, parentTempId) => {
                  localNodes.value.forEach(n => {
                    const nId = n.id || n.tempId
                    if (nodesToRemove.has(nId)) return
                    if (
                      (parentId && n.parentId === parentId) ||
                      (parentTempId && n.parentTempId === parentTempId)
                    ) {
                      nodesToRemove.add(nId)
                      markDescendants(n.id, n.tempId)
                    }
                  })
                }

                // Mark direct children whose parentOpsi was removed
                localNodes.value.forEach(n => {
                  const nId = n.id || n.tempId
                  if (nodesToRemove.has(nId)) return
                  const matchesParent =
                    (editing.id && n.parentId === editing.id) ||
                    (editing.tempId && n.parentTempId === editing.tempId)
                  if (matchesParent && removedOpsi.includes(n.parentOpsi)) {
                    nodesToRemove.add(nId)
                    markDescendants(n.id, n.tempId)
                  }
                })

                if (nodesToRemove.size > 0) {
                  localNodes.value = localNodes.value.filter(
                    n => !nodesToRemove.has(n.id || n.tempId),
                  )
                }
              }
            }
          }
          message.success('Klasifikasi berhasil diupdate')
        } else {
          localNodes.value.push({
            tempId: generateTempId(),
            ...nodeData,
          })
          message.success('Klasifikasi berhasil ditambahkan')
        }
        done()
      } catch (err) {
        console.error('Error in handleNodeModalSubmit', err)
        error()
      }
    }

    const handleDeleteNode = node => {
      nodeToDelete.value = node
      deleteModalVisible.value = true
    }

    const confirmDeleteNode = async () => {
      const node = nodeToDelete.value
      if (!node) return

      deleteModalLoading.value = true

      if (node.id && algoritmaId.value) {
        try {
          await mobileCmsAlgoritmaAPI.deleteNode(node.id)
        } catch (error) {
          console.error('Failed to delete node from server', error)
        }
      }

      // Collect all nodes to remove (node + all descendants)
      const nodesToRemove = new Set()
      const nodeId = node.id || node.tempId
      nodesToRemove.add(nodeId)

      // Recursively find all descendants
      const findDescendants = (parentId, parentTempId) => {
        localNodes.value.forEach(n => {
          const nId = n.id || n.tempId
          if (nodesToRemove.has(nId)) return // Already marked for removal

          if (
            (parentId && n.parentId === parentId) ||
            (parentTempId && n.parentTempId === parentTempId)
          ) {
            nodesToRemove.add(nId)
            findDescendants(n.id, n.tempId)
          }
        })
      }

      findDescendants(node.id, node.tempId)

      // Remove all marked nodes
      localNodes.value = localNodes.value.filter(n => {
        const nId = n.id || n.tempId
        return !nodesToRemove.has(nId)
      })

      deleteModalLoading.value = false
      deleteModalVisible.value = false
      nodeToDelete.value = null
      message.success('Klasifikasi berhasil dihapus')
    }

    const cancelDelete = () => {
      deleteModalVisible.value = false
      nodeToDelete.value = null
    }

    const showPreview = () => {
      previewVisible.value = true
    }

    const showDiagramPreview = () => {
      diagramPreviewVisible.value = true
    }

    const createNodesRecursively = async (
      algId,
      nodes,
      parentId = null,
      parentTempIdMap = {},
      createdTempIds = new Set(),
    ) => {
      console.log('=== createNodesRecursively ===')
      console.log('algId:', algId)
      console.log('nodes count:', nodes.length)
      console.log('parentId:', parentId)
      console.log('parentTempIdMap:', parentTempIdMap)
      console.log('createdTempIds:', createdTempIds)

      const newParentTempIdMap = { ...parentTempIdMap }
      const newCreatedTempIds = new Set(createdTempIds)

      const nodesToCreate = nodes.filter(n => {
        // Skip if already created
        if (n.tempId && createdTempIds.has(n.tempId)) {
          console.log('Skipping already created node:', n.tempId)
          return false
        }

        // If parentId is specified, only get children of that parent
        if (parentId) {
          // Match by actual parentId (for existing nodes)
          if (n.parentId === parentId) {
            console.log('Found child by parentId:', n.namaKlasifikasi, 'parentId:', parentId)
            return true
          }
          // Match by parentTempId (for new nodes whose parent was just created)
          if (n.parentTempId && parentTempIdMap[n.parentTempId] === parentId) {
            console.log(
              'Found child by parentTempId:',
              n.namaKlasifikasi,
              'parentTempId:',
              n.parentTempId,
            )
            return true
          }
          return false
        }

        // Root nodes (no parent at all) - only on first call
        if (!n.parentId && !n.parentTempId) {
          console.log('Found root node:', n.namaKlasifikasi)
          return true
        }

        // Node with parentId pointing to existing database node
        if (n.parentId && !n.parentId.startsWith('temp_')) {
          console.log(
            'Found node with existing DB parent:',
            n.namaKlasifikasi,
            'parentId:',
            n.parentId,
          )
          return true
        }

        return false
      })

      console.log('nodesToCreate:', nodesToCreate.length, JSON.parse(JSON.stringify(nodesToCreate)))

      for (const node of nodesToCreate) {
        // Determine the actual parent ID
        let actualParentId = node.parentId || null
        if (node.parentTempId && parentTempIdMap[node.parentTempId]) {
          actualParentId = parentTempIdMap[node.parentTempId]
        }

        const data = {
          alurAlgoritmaKlinisId: algId,
          parentId: actualParentId,
          parentOpsi: node.parentOpsi,
          namaKlasifikasi: node.namaKlasifikasi,
          opsiKlasifikasi: node.opsiKlasifikasi,
          ditandaiTerakhir: node.ditandaiTerakhir,
          algoritmaTerkaitId: node.algoritmaTerkaitId,
          kalkulatorTerkaitId: node.kalkulatorTerkaitId,
        }

        console.log('Creating node with data:', JSON.parse(JSON.stringify(data)))

        try {
          const response = await mobileCmsAlgoritmaAPI.createNode(data)
          console.log('Node created:', response.data)
          const newNodeId = response.data.data?.id

          if (node.tempId && newNodeId) {
            newParentTempIdMap[node.tempId] = newNodeId
            newCreatedTempIds.add(node.tempId)
            console.log('Mapped tempId to realId:', node.tempId, '->', newNodeId)

            // Recursively create children of this node
            await createNodesRecursively(
              algId,
              nodes,
              newNodeId,
              newParentTempIdMap,
              newCreatedTempIds,
            )
          } else if (!node.tempId && newNodeId) {
            // Node with actual ID (shouldn't happen in create mode, but just in case)
            await createNodesRecursively(
              algId,
              nodes,
              newNodeId,
              newParentTempIdMap,
              newCreatedTempIds,
            )
          }
        } catch (error) {
          console.error('Failed to create node', error)
          console.error('Failed data was:', JSON.parse(JSON.stringify(data)))
        }
      }
    }

    const handleSubmit = async () => {
      if (!form.nama.trim()) {
        message.error('Nama alur algoritma klinis wajib diisi')
        return
      }

      if (localNodes.value.length === 0) {
        message.error('Struktur klasifikasi wajib diisi minimal 1 klasifikasi')
        return
      }

      submitting.value = true
      try {
        const fd = new FormData()
        fd.append('nama', form.nama.trim())
        if (form.deskripsi.trim()) fd.append('deskripsi', form.deskripsi.trim())
        if (form.imageFile) fd.append('imageDiagram', form.imageFile)

        const allNodes = JSON.parse(JSON.stringify(localNodes.value))
        console.log('=== [BUG_TRACE] SIMPAN CLICKED ===')
        console.log('[BUG_TRACE] isEdit:', isEdit.value)
        console.log('[BUG_TRACE] algoritmaId:', algoritmaId.value)
        console.log('[BUG_TRACE] totalNodes:', allNodes.length)
        console.log('[BUG_TRACE] ALL NODES FULL DUMP:')
        allNodes.forEach((n, i) => {
          console.log(
            `  [${i}] id=${n.id ?? 'null'} tempId=${n.tempId ?? 'null'} parentId=${n.parentId ??
              'null'} parentTempId=${n.parentTempId ?? 'null'} parentOpsi="${n.parentOpsi ??
              ''}" nama="${n.namaKlasifikasi}" opsi=[${(n.opsiKlasifikasi || []).join(
              ', ',
            )}] terakhir=${n.ditandaiTerakhir}`,
          )
        })

        if (isEdit.value) {
          const newNodes = localNodes.value.filter(n => !n.id && n.tempId)
          console.log(
            '[BUG_TRACE] UPDATE MODE - newNodes (tempId only):',
            JSON.parse(JSON.stringify(newNodes)),
          )

          await mobileCmsAlgoritmaAPI.update(algoritmaId.value, fd)

          if (newNodes.length > 0) {
            await createNodesRecursively(algoritmaId.value, newNodes)
          }

          // Fetch final state from server and log it
          try {
            const finalResp = await mobileCmsAlgoritmaAPI.getById(algoritmaId.value)
            const finalNodes = finalResp.data?.data?.nodes || []
            console.log('[BUG_TRACE] FINAL STATE ON SERVER after save:')
            finalNodes.forEach((n, i) => {
              console.log(
                `  [${i}] id=${n.id ?? 'null'} parentId=${n.parentId ??
                  'null'} parentOpsi="${n.parentOpsi ?? ''}" nama="${n.namaKlasifikasi}" opsi=[${(
                  n.opsiKlasifikasi || []
                ).join(', ')}] terakhir=${n.ditandaiTerakhir}`,
              )
            })
          } catch (e) {
            console.warn('[BUG_TRACE] Could not fetch final state:', e)
          }

          message.success('Algoritma berhasil diupdate')
        } else {
          console.log('[BUG_TRACE] CREATE MODE')
          const response = await mobileCmsAlgoritmaAPI.create(fd)
          const newAlgoritmaId = response.data.data?.id || response.data.data?.algoritma?.id
          console.log('[BUG_TRACE] newAlgoritmaId:', newAlgoritmaId)

          if (localNodes.value.length > 0 && newAlgoritmaId) {
            await createNodesRecursively(newAlgoritmaId, localNodes.value)
          }

          // Fetch final state from server and log it
          try {
            const finalResp = await mobileCmsAlgoritmaAPI.getById(newAlgoritmaId)
            const finalNodes = finalResp.data?.data?.nodes || []
            console.log('[BUG_TRACE] FINAL STATE ON SERVER after save:')
            finalNodes.forEach((n, i) => {
              console.log(
                `  [${i}] id=${n.id ?? 'null'} parentId=${n.parentId ??
                  'null'} parentOpsi="${n.parentOpsi ?? ''}" nama="${n.namaKlasifikasi}" opsi=[${(
                  n.opsiKlasifikasi || []
                ).join(', ')}] terakhir=${n.ditandaiTerakhir}`,
              )
            })
          } catch (e) {
            console.warn('[BUG_TRACE] Could not fetch final state:', e)
          }

          message.success('Algoritma berhasil dibuat')
        }

        captureInitialSnapshot()
        router.push('/mobile-cms/algoritma-klinis')
      } catch (error) {
        console.error('ERROR:', error)
        const msg = error.response?.data?.message || 'Terjadi kesalahan'
        message.error(msg)
      } finally {
        submitting.value = false
      }
    }

    const goBack = () => {
      if (confirmLeaveIfNeeded()) {
        router.push('/mobile-cms/algoritma-klinis')
      }
    }

    onMounted(() => {
      window.addEventListener('beforeunload', handleBeforeUnload)
      if (isEdit.value) {
        loadAlgoritma()
      } else {
        captureInitialSnapshot()
      }
      loadAlgoritmaList()
      loadKalkulatorList()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    })

    const splitPath = pathLabel => {
      if (!pathLabel) return []
      return pathLabel.split(' -> ').filter(p => p.trim())
    }

    // ── Preview Flow ──────────────────────────────────────────────
    const previewScrollAnchor = ref(null)
    const previewActiveCards = ref([])
    const previewSelectedOptions = ref({})

    const previewFindChildren = node => {
      return localNodes.value.filter(
        k =>
          (k.parentId && (k.parentId === node.id || k.parentId === node.tempId)) ||
          (k.parentTempId && (k.parentTempId === node.tempId || k.parentTempId === node.id)),
      )
    }

    const previewFindNextCard = (currentNode, visited = new Set()) => {
      const nodeId = currentNode.id || currentNode.tempId
      if (visited.has(nodeId)) return
      const newVisited = new Set(visited)
      newVisited.add(nodeId)
      const children = previewFindChildren(currentNode)
      if (children.length > 0) {
        const nextNode = children[0]
        previewActiveCards.value.push(nextNode)
        if (!nextNode.opsiKlasifikasi?.length && !nextNode.ditandaiTerakhir) {
          previewFindNextCard(nextNode, newVisited)
        }
      }
    }

    const previewInitFlow = () => {
      previewActiveCards.value = []
      previewSelectedOptions.value = {}
      const rootNodes = localNodes.value.filter(n => !n.parentId && !n.parentTempId)
      if (rootNodes.length === 0) return
      const root = rootNodes[0]
      previewActiveCards.value = [root]
      if (!root.opsiKlasifikasi?.length && !root.ditandaiTerakhir) {
        previewFindNextCard(root, new Set())
      }
    }

    const previewSelectOption = (cardIndex, optionIndex) => {
      const node = previewActiveCards.value[cardIndex]
      if (!node) return
      if (previewSelectedOptions.value[cardIndex] === optionIndex) return
      if (optionIndex >= (node.opsiKlasifikasi?.length || 0)) return
      const selectedLabel = node.opsiKlasifikasi[optionIndex]
      const allChildren = previewFindChildren(node)
      let selectedChild = allChildren.find(k => k.parentOpsi === selectedLabel) || null
      if (!selectedChild)
        selectedChild = optionIndex < allChildren.length ? allChildren[optionIndex] : null
      if (!selectedChild) return
      const newSelections = {}
      for (const key in previewSelectedOptions.value) {
        if (Number(key) <= cardIndex) newSelections[key] = previewSelectedOptions.value[key]
      }
      newSelections[cardIndex] = optionIndex
      previewSelectedOptions.value = newSelections
      previewActiveCards.value = previewActiveCards.value.slice(0, cardIndex + 1)
      previewActiveCards.value.push(selectedChild)
      if (!selectedChild.opsiKlasifikasi?.length && !selectedChild.ditandaiTerakhir) {
        previewFindNextCard(selectedChild, new Set())
      }
      nextTick(() => {
        previewScrollAnchor.value?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      })
    }

    const previewResetFlow = () => previewInitFlow()

    watch(previewVisible, val => {
      if (val) previewInitFlow()
    })

    return {
      splitPath,
      algoritmaId,
      isEdit,
      initialLoading,
      submitting,
      formRef,
      form,
      getFileUrl,
      removeImage,
      removeCurrentImage,
      onBeforeUpload,
      handleSubmit,
      // Nodes
      localNodes,
      algoritmaList,
      kalkulatorList,
      rootNodes,
      levels,
      filteredAlgoritmaList,
      // Node Modal
      nodeModalVisible,
      editingNode,
      parentNodeForModal,
      parentOpsiForModal,
      showCreateNodeModal,
      showEditNodeModal,
      handleNodeModalSubmit,
      handleDeleteNode,
      // Delete Modal
      deleteModalVisible,
      deleteModalLoading,
      confirmDeleteNode,
      cancelDelete,
      // Preview
      previewVisible,
      showPreview,
      diagramPreviewVisible,
      showDiagramPreview,
      getAlgoritmaNamePreview,
      previewScrollAnchor,
      previewActiveCards,
      previewSelectedOptions,
      previewSelectOption,
      previewResetFlow,
      // Other
      goBack,
    }
  },
}
</script>

<style scoped>
.algoritma-form {
  padding: 0;
  background: #fff;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  background: #fcfcfc;
  padding-bottom: 24px;
  h1 {
    font-size: 24px;
  }
}

.back-btn {
  margin-right: 16px;
  border: none;
  box-shadow: none;
}

.page-header h1 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.form-content {
  padding: 24px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.form-label .required {
  color: #e10f28;
}

.nama-input {
  width: 100%;
}

.upload-section {
  display: flex;
  align-items: flex-start;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  font-weight: 700;
  font-size: 14px;
  font-family: 'Barlow', sans-serif !important;
  transition: all 0.2s;
}

.upload-btn:hover {
  background: #fef2f2;
}

.current-image,
.preview-image {
  display: flex;
  align-items: center;
  gap: 16px;
}

.current-image img,
.preview-image img {
  max-height: 120px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.tree-container {
  background: #fff;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  box-shadow: none;
}

.empty-tree {
  padding: 0;
}

.tree-table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
}

.tree-table thead {
  display: table-header-group;
}

.tree-table thead th {
  background: #f2f4f8 !important;
  padding: 16px 24px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  border-bottom: 1px solid #e5e7eb;
  border-right: none !important;
}

.tree-table tbody tr {
  cursor: default;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f3f4f6;
}

.tree-table tbody tr:hover {
  background-color: #f5f5f5;
}

.tree-table tbody td {
  padding: 16px 24px;
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid #f3f4f6;
  border-right: none !important;
}

.tree-table tbody tr:last-child td {
  border-bottom: none;
}

.col-tingkat,
.td-tingkat {
  width: 120px;
  font-size: 14px;
  font-weight: 400;
  color: #444444;
}

.col-klasifikasi,
.td-klasifikasi {
  width: auto;
}

.klasifikasi-items {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.klasifikasi-item {
  display: block;
}

.path-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.path-badge {
  display: inline-block;
  background: #f2f4f8;
  color: var(--Mobile-Secondary, #2f318b);
  padding: 4px 12px;
  border-radius: 16px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
}

.path-arrow {
  color: #9ca3af;
  font-size: 10px;
}

.branch-content {
  margin-top: 12px;
}

.branch-item {
  margin-bottom: 16px;
}

.preview-tree .branch-item .path-badges {
  margin-top: 8px;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border-radius: 50px;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 700;
  transition: all 0.3s;
}

.add-btn:hover {
  background: #fef2f2;
}

.level-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 12px 0;
}

.preview-card {
  background: #fff;
  border: 1px solid #2f318b;
  border-radius: 12px;
  padding: 16px;
  min-width: 200px;
  width: fit-content;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-card .card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.preview-card .card-header-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.preview-card .card-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
}

.preview-card .card-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.preview-card .card-options .opsi-badge {
  border-radius: 20px;
  background: #2f318b;
  color: #f9fafb;
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 6px 14px;
  border: 1px solid #2f318b;
}

.preview-card .card-tags {
  margin-top: 12px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.preview-card .card-tags :deep(.ant-tag) {
  margin: 0;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
}

.empty-branch {
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}

.tree-content {
  flex: 1;
  overflow: visible;
}

.tree-levels {
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;
}

.action-buttons {
  display: flex;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
}

.save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  min-width: 120px;
  border-radius: 50px;
  font-weight: 700;
  padding: 8px 32px;
  background: #e10f28;
  border: 2px solid #e10f28;
  color: white;
  transition: all 0.3s;
}

.save-btn:hover {
  background: white;
  border-color: #e10f28;
  color: #e10f28;
}

.preview-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  border: 2px solid #e10f28;
  background: white;
  color: #e10f28;
  border-radius: 50px;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 700;
  padding: 0 24px;
  transition: all 0.3s;
}

.preview-btn:hover {
  background: #fef2f2;
}

.opsi-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.opsi-item {
  display: flex;
  gap: 8px;
}

.opsi-item .a-input {
  flex: 1;
}

.opsi-badge {
  border-radius: 20px;
  background: #2f318b;
  color: #f9fafb;
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 6px 14px;
  border: 1px solid #2f318b;
}

.preview-container {
  max-height: 500px;
  overflow-y: auto;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
}

.preview-empty {
  text-align: center;
  color: #6b7280;
  padding: 40px;
  font-size: 14px;
}

.preview-tree {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.image-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
}

.diagram-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  background: #f9fafb;
  border-radius: 8px;
  max-height: 70vh;
  overflow: auto;
}

.diagram-preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
}

/* ── Mobile Preview Flow ── */
.mobile-preview-container {
  max-height: 70vh;
  overflow-y: auto;
  padding: 16px 20px;
  background: #fcfcff;
  border-radius: 8px;
}
.mobile-preview-empty {
  text-align: center;
  color: #6b7280;
  padding: 40px;
  font-size: 14px;
}
.mobile-preview-flow {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.mobile-preview-toolbar {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
}
.mobile-preview-ulang-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: 30px;
  background: #e10f28;
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 8px 10px rgba(0, 0, 0, 0.05);
}
.mobile-preview-ulang-btn:hover {
  background: #b80d22;
}
.mobile-preview-card {
  width: 100%;
  border-radius: 16px;
  padding: 24px;
  box-sizing: border-box;
}
.mobile-preview-card-title {
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #444444;
  margin: 0;
}
.mobile-preview-card--end {
  background: #2f318b;
  box-shadow: 0 4px 12px rgba(47, 49, 139, 0.3);
}
.mobile-preview-card--end .mobile-preview-card-title {
  color: #fff;
  font-weight: 700;
  font-size: 16px;
}
.mobile-preview-end-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}
.mobile-preview-end-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}
.mobile-preview-card--options {
  background: #ffffff;
  border: 2px solid #2f318b;
  box-shadow: 0 4px 12px rgba(47, 49, 139, 0.15);
}
.mobile-preview-card--no-options {
  background: #ffffff;
  border: 2px solid #c34f6b;
  box-shadow: 0 4px 12px rgba(195, 79, 107, 0.15);
}
.mobile-preview-options-row {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}
.mobile-preview-option-btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 16px;
  border: none;
  background: #ffffff;
  color: #444444;
  font-family: Lato, sans-serif;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: background 0.15s, color 0.15s;
  word-break: break-word;
}
.mobile-preview-option-btn--selected {
  background: #2f318b;
  color: #ffffff;
}
.mobile-preview-option-btn:hover:not(.mobile-preview-option-btn--selected) {
  background: #f0f1ff;
}
.mobile-preview-arrow {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 0;
}
.mobile-preview-arrow--blue {
  color: #2f318b;
}
.mobile-preview-arrow--pink {
  color: #c34f6b;
}

:deep(.ant-input) {
  border-radius: 8px;
  border-color: #d1d5db;
  font-size: 14px;
}
:deep(.ant-input::placeholder) {
  color: #798892 !important;
}
</style>
